import i18n from '../i18n';

// ─── Fechas, horas y montos en el idioma de la APP ──────────────────────────
//
// EL PROBLEMA, EN DOS MITADES.
//
// La primera la vio el director en Cumplimiento CAP: `toLocaleDateString()` SIN
// locale usa el del NAVEGADOR. Con la app en español y Chrome en inglés eso
// imprime `9/17/2026` en medio de una oración en español, y en el caso ambiguo
// (`3/9/2026`) el lector no puede saber si es marzo o septiembre.
//
// La segunda es la misma al revés, y era la mayoría: ~85 llamadas con el locale
// ESCRITO A MANO —`'es-CL'`, `'es'`, `'es-MX'`, tres grafías del mismo idioma—
// que imprimen en español aunque el usuario haya puesto la app en inglés. Los
// dos defectos tienen una sola causa: el formato se decidía en el sitio de la
// llamada, y ahí nadie sabe en qué idioma está leyendo la persona.
//
// LA REGLA. El locale es una propiedad de QUIÉN LEE, igual que el texto (ver
// CLAUDE.md, "el alcance se define por QUIÉN LEE"). En pantalla quien lee es el
// usuario, y su idioma lo controla `uiStore` — el detector del navegador está
// desactivado a propósito. Entonces el formato sigue a i18n, sin excepción.
//
// LA EXCEPCIÓN, QUE ES EXPLÍCITA. Un documento con audiencia propia no sigue al
// lector de la pantalla: la propuesta CAP y sus anexos se le entregan a TMAI en
// inglés, y su fecha va en inglés aunque el coordinador tenga la app en español.
// Eso se declara con `inLocale('en-US')`, que se lee como lo que es —un idioma
// fijado a propósito— en vez de un `'en-US'` suelto indistinguible de un olvido.
//
// Un test (`__tests__/locale-guard.spec.ts`) impide que vuelvan a aparecer
// llamadas crudas fuera de este archivo.

/** i18n habla en idiomas (`es`, `en`); `Intl` quiere locales. El `es-CL` no es
 *  decorativo: fija día/mes/año, que es lo que el resto de la app ya usa. */
export function dateLocale(language: string | undefined): string {
  const lang = language ?? 'es';
  if (lang.includes('-')) return lang;
  const base = lang.toLowerCase();
  if (base === 'es') return 'es-CL';
  if (base === 'en') return 'en-US';
  return lang;
}

/** Lo que llega de Firestore no siempre es una `Date`: hay ISO strings, epochs y
 *  `Timestamp`s. Un valor vacío o inválido devuelve '' — nunca "Invalid Date"
 *  impreso en pantalla. */
export type DateLike = Date | string | number | { toDate: () => Date } | null | undefined;

/** `YYYY-MM-DD` es una fecha de CALENDARIO, no un instante. `new Date('2026-03-01')`
 *  la interpreta como medianoche UTC, y mostrada en Chile (UTC-3/-4) retrocede al
 *  28 de febrero: el ciclo empezaría un día antes en pantalla que en la base.
 *  Por eso se arma a mano, en hora local. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function toDate(value: DateLike): Date | null {
  if (value === null || value === undefined || value === '') return null;
  let d: Date;
  if (value instanceof Date) {
    d = value;
  } else if (typeof value === 'object' && typeof value.toDate === 'function') {
    d = value.toDate();
  } else if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const [, y, m, day] = DATE_ONLY.exec(value)!;
    d = new Date(Number(y), Number(m) - 1, Number(day));
  } else {
    d = new Date(value as string | number);
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Un monto sin moneda no es un monto: el mismo 1.500 es dos precios distintos
 *  según la moneda del arancel. Por eso `currency` es obligatoria. */
function money(
  locale: string,
  amount: number | null | undefined,
  currency: string,
  options?: Intl.NumberFormatOptions,
): string {
  const n = Number(amount);
  return (Number.isFinite(n) ? n : 0).toLocaleString(locale, {
    style: 'currency',
    currency,
    ...options,
  });
}

// ─── En el idioma de la app (lo que va en pantalla) ─────────────────────────

export function formatDate(value: DateLike, options?: Intl.DateTimeFormatOptions): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString(dateLocale(i18n.language), options);
}

export function formatDateTime(value: DateLike, options?: Intl.DateTimeFormatOptions): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleString(dateLocale(i18n.language), options);
}

export function formatTime(value: DateLike, options?: Intl.DateTimeFormatOptions): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleTimeString(dateLocale(i18n.language), options);
}

export function formatMoney(
  amount: number | null | undefined,
  currency = 'CLP',
  options?: Intl.NumberFormatOptions,
): string {
  return money(dateLocale(i18n.language), amount, currency, options);
}

/** Números sin moneda (cantidades, porcentajes con su signo aparte). El
 *  separador de miles también cambia por idioma: `1,234` vs `1.234`. */
export function formatNumber(
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions,
): string {
  const n = Number(value);
  return (Number.isFinite(n) ? n : 0).toLocaleString(dateLocale(i18n.language), options);
}

// ─── En un idioma FIJADO a propósito (documentos con audiencia propia) ──────

export interface PinnedFormatters {
  date: (value: DateLike, options?: Intl.DateTimeFormatOptions) => string;
  dateTime: (value: DateLike, options?: Intl.DateTimeFormatOptions) => string;
  time: (value: DateLike, options?: Intl.DateTimeFormatOptions) => string;
  money: (
    amount: number | null | undefined,
    currency?: string,
    options?: Intl.NumberFormatOptions,
  ) => string;
}

/** Para lo que NO lo lee el usuario de la pantalla: la propuesta CAP y sus
 *  anexos se entregan a TMAI en inglés. Fijar el idioma acá es correcto; lo
 *  incorrecto era que no se distinguiera de un descuido. */
export function inLocale(locale: string): PinnedFormatters {
  return {
    date: (value, options) => {
      const d = toDate(value);
      return d ? d.toLocaleDateString(locale, options) : '';
    },
    dateTime: (value, options) => {
      const d = toDate(value);
      return d ? d.toLocaleString(locale, options) : '';
    },
    time: (value, options) => {
      const d = toDate(value);
      return d ? d.toLocaleTimeString(locale, options) : '';
    },
    money: (amount, currency = 'CLP', options) => money(locale, amount, currency, options),
  };
}
